<template>
  <div>
    <v-toolbar dense :class="['mb-4']">
      <v-container grid-list-md :class="['pa-0']">
        <v-layout row wrap justify-end>
          <v-flex xs6 sm4 md2>
            <v-btn aria-label="share" :class="['mr-2']" small block dark :color="getColorScheme()" @click="getPermalink()" :title="$lang.copyChartUrl">
              <v-icon :class="['mr-2']">mdi-share-variant</v-icon>
              <span v-html="$lang.copyChartUrl" />
            </v-btn>
          </v-flex>
          <v-flex xs6 sm4 md2>
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn :disabled="exportButtonDisabled" :loading="exportButtonDisabled" aria-label="export" :class="['ml-2']" block small dark v-on="on" :color="getColorScheme()" :title="$lang.exportChart">
                  <v-icon :class="['mr-2']">mdi-download</v-icon>
                  <span v-html="$lang.exportChart" />
                </v-btn>
              </template>
              <v-list dense>
                <v-list-tile :disabled="!option.enabled" v-for="(option, index) in $config.chartExportOptions" :key="index" @click="exportChart(option.type)">
                  <v-list-tile-avatar>
                    <v-icon>{{ option.icon }}</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-title v-html="option.label" />
                </v-list-tile>
              </v-list>
            </v-menu>
          </v-flex>
        </v-layout>
      </v-container>
    </v-toolbar>
    <v-card :class="['bpi-chart']" v-if="chartdata.labels && chartdata.labels.length">
      <v-card-text>
        <line-chart :chartdata="chartdata" :options="options" />
      </v-card-text>
      <v-card-title>{{ disclaimer }} {{ $lang.lastUpdated }} {{ moment(updated).format('dddd, MMMM Do YYYY') }}.</v-card-title>
    </v-card>
    <v-snackbar v-model="permalinkCopied">
      <span v-html="$lang.permalinkCopied" />
      <v-btn aria-label="close" flat :class="[`${getColorScheme()}--text`]" :title="$lang.close" dark @click="permalinkCopied = false" v-html="$lang.close" />
    </v-snackbar>
  </div>
</template>
<script>
import LineChart from '../components/line-chart'
import methods from '../mixins/methods'
import htmlToImage from 'html-to-image'
import copy from 'copy-to-clipboard'
import { saveAs } from 'file-saver'
import moment from 'moment'
import Papa from 'papaparse'

export default {
  name: 'Bpi',
  components: {
    LineChart
  },
  mixins: [
    methods
  ],
  data () {
    return {
      moment,
      start: [],
      end: [],
      permalinkCopied: false,
      exportButtonDisabled: false,
      disclaimer: null,
      updated: null,
      chartdata: {
        labels: [],
        datasets: []
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        reactive: true
      },
      exportableData: {
        datasets: [],
        labels: []
      }
    }
  },
  mounted () {
    this.onMounted()
  },
  watch: {
    '$route.path' () {
      this.parseRouteParams()
    }
  },
  methods: {
    /**
     * Handler for the $mounted event of the component
     * @method onMounted
     * @return {void}
     */
    onMounted () {
      this.parseRouteParams()
    },
    /**
     * Export the chart in the selected format
     * @method exportChart
     * @param {string} format [format = 'png']
     * @return {void}
     */
    exportChart (format = 'png') {
      const fileName = `${this.$config.exportFilename}.${format}`
      const chartNode = this.getChartNode()
      const textPlain = {
        type: 'text/plain;charset=utf-8'
      }
      const exportableData = this.exportableData
      const dataSets = exportableData.datasets
      this.exportButtonDisabled = true
      switch (format) {
        case 'png':
          htmlToImage.toPng(chartNode).then(blob => {
            saveAs(blob, fileName)
            this.exportButtonDisabled = false
          })
          break
        case 'json':
          const blob = new Blob([JSON.stringify(exportableData, null, 2)], textPlain)
          saveAs(blob, fileName)
          this.exportButtonDisabled = false
          break
        case 'csv':
          const csv = []
          exportableData.labels.forEach(label => {
            csv.push([label])
          })
          dataSets.forEach((set, index) => {
            dataSets[index].forEach((nestedSet, nestedIndex) => {
              csv[nestedIndex].push(nestedSet)
            })
          })
          saveAs(new Blob([Papa.unparse(csv)], textPlain), fileName)
          this.exportButtonDisabled = false
          break
      }
    },
    /**
     * Copy the chart url for sharing
     * @method getPermalink
     * @return {void}
     */
    getPermalink () {
      copy(window.location.href)
      this.permalinkCopied = true
    },
    /**
     * Get the HTML node that will be used to create an image from
     * @method getChartNode
     * @return {Element}
     */
    getChartNode () {
      return document.querySelector('.bpi-chart')
    },
    /**
     * Parse route parameters and get the BPI data
     * @method parseRouteParams
     * @return {void}
     */
    parseRouteParams () {
      this.start = JSON.parse(this.$route.params.start)
      this.end = JSON.parse(this.$route.params.end)
      this.getBpiData({start: this.start, end: this.end})
    },
    /**
     * Get CoinDesk BPI data
     * @method getBpiData
     * @param {object} range [range = {start: [], end: []}]
     * @return {void}
     */
    getBpiData (range = {start: [], end: []}) {
      let labels = []
      let datasets = []
      let count = 0
      const chartColors = this.$colors.chartColors
      this.exportableData = {
        datasets: [],
        labels: []
      }
      range.start.forEach((start, index) => {
        this.$http({
          url: this.$api.bpi.url(start, range.end[index]),
          method: this.$api.bpi.methods[0]
        }).then(response => {
          if (response.data) {
            this.disclaimer = response.data.disclaimer
            this.updated = response.data.time
            labels = Object.keys(response.data.bpi)
            const color = chartColors[parseInt(start.split('-')[0], 10)] || chartColors[2010]
            this.exportableData.datasets.push(Object.values(response.data.bpi))
            datasets.push({
              label: `${start} - ${range.end[index]}`,
              borderWidth: 1,
              backgroundColor: 'transparent',
              pointBackgroundColor: color,
              borderColor: color,
              data: Object.values(response.data.bpi)
            })
            count++
            if (count === range.start.length) {
              this.chartdata.labels = []
              this.chartdata.datasets = []
              this.$nextTick(() => {
                this.chartdata.datasets = datasets
                this.chartdata.labels = labels
                this.exportableData.labels = labels
              })
            }
          }
        }).catch(error => {
          console.error(error)
        })
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/bpi.vue